import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthContext";
import { api, ApiError } from "../../lib/api";
import "./ClubPanel.css";

/** Organizador sin club: crea SU club y queda como su staff (Eduardo
 *  2026-10-13: el admin «es» el club, no entra como jugador). */
export function ClubActivateContent() {
  const { token, user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", contact_email: user?.email ?? "", city: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Solo organizadores (admin) y superadmin crean clubes; un jugador solo
  // se afilia a uno existente (Eduardo 2026-10-17). El backend responde 403.
  const canCreateClub = Boolean(user?.roles?.some((role) => role === "admin" || role === "superadmin"));

  if (user?.roles?.includes("club")) {
    return <Navigate to="/club" replace />;
  }

  async function goToPanel() {
    await refresh();
    navigate("/club", { replace: true });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.clubs.createMine(token, {
        name: form.name.trim(),
        contact_email: form.contact_email.trim() || null,
        city: form.city.trim() || null,
      });
      await goToPanel();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && /Ya administras/.test(err.message)) {
        await goToPanel();
        return;
      }
      setError(err instanceof ApiError ? err.message : "No se pudo crear el club.");
      setSaving(false);
    }
  }

  if (!canCreateClub) {
    return (
      <div className="club-panel club-activate">
        <section className="club-panel__card" aria-label="Afíliate a un club">
          <div className="club-panel__card-head">
            <h2>Afíliate a un club</h2>
          </div>
          <p>Los clubes los dan de alta sus organizadores. Busca el tuyo y solicita tu afiliación.</p>
          <div>
            <Link className="club-panel__lime-btn" to="/player/clubs">
              Buscar clubes
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="club-panel club-activate">
      <section className="club-panel__hero">
        <div>
          <p className="club-panel__eyebrow">Club</p>
          <h1>Activa tu club en Torneos123</h1>
          <p>
            Maneja canchas, socios y reservas en el mismo lugar que tus torneos. Tú quedas como administrador del
            club.
          </p>
        </div>
      </section>
      <form className="club-panel__card" onSubmit={handleSubmit} aria-label="Crear mi club">
        <div className="club-panel__card-head">
          <h2>Crear mi club</h2>
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="club-config__inline">
          <label>
            Nombre del club
            <input
              type="text"
              required
              value={form.name}
              disabled={saving}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            Correo de contacto
            <input
              type="email"
              value={form.contact_email}
              disabled={saving}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            />
          </label>
          <label>
            Ciudad (opcional)
            <input
              type="text"
              value={form.city}
              disabled={saving}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </label>
        </div>
        <div>
          <button type="submit" className="club-panel__lime-btn" disabled={saving || !form.name.trim()}>
            {saving ? "Creando…" : "Crear mi club"}
          </button>
        </div>
      </form>
      <section className="club-panel__card">
        <div className="club-panel__card-head">
          <h2>Qué incluye</h2>
        </div>
        <ul>
          <li>Agenda de canchas del día con reservas, partidos de torneo y bloqueos.</li>
          <li>Padrón de socios con solicitudes, link de invitación y credencial.</li>
          <li>Reglas de reserva para que nadie acapare las canchas.</li>
        </ul>
      </section>
    </div>
  );
}

export default function ClubActivatePage() {
  return <ClubActivateContent />;
}
